import { EdidProfile, KvmHardwareState, QualityProfile, StreamTelemetryMetrics } from '../../shared/types';
import { NxkvPacketPool } from './ObjectPool';

interface StreamLayer {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

interface ActiveImageStream {
  layerIdx: number;
  x: number;
  y: number;
  mime: string;
  chunks: string[];
}

export interface HardwareFrameSnapshot {
  nodeId: string;
  hostname: string;
  ip: string;
  osVersion: string;
  cpuModel: string;
  cpuPercent: number;
  ramUsedMb: number;
  ramTotalMb: number;
  hardware: KvmHardwareState;
  edid: EdidProfile;
  quality: QualityProfile;
  bios: {
    mode: 'OS_CONSOLE' | 'BIOS_POST' | 'BIOS_SETUP' | 'BOOT_MENU' | 'ISO_LIVE_BOOT';
    selectedTab: number;
    selectedRow: number;
    typedBuffer: string;
    lastKeyLabel: string;
    bootIsoName: string;
  };
  serverTimestamp: number;
}

export class KvmStreamClient {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly packetPool = new NxkvPacketPool(32, 128);
  private readonly textDecoder = new TextDecoder('utf-8');

  private ws: WebSocket | null = null;
  private nodeId: string;
  private engineMode: 'auto' | 'rdp' | 'kvm' = 'auto';
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private rafId: number | null = null;
  private destroyed = false;

  private layers: Map<number, StreamLayer> = new Map();
  private imageStreams: Map<number, ActiveImageStream> = new Map();
  private instructionChain: Promise<void> = Promise.resolve();
  private hasRdpFrames = false;
  private latestHwSnapshot: HardwareFrameSnapshot | null = null;
  private measuredRttMs = 0.6;
  private rttHistory: number[] = [0.7, 0.8, 0.6, 0.7, 0.6, 0.8, 0.5, 0.7];
  private frameCounter = 0;
  private lastFpsTick = performance.now();
  private currentFps = 60;
  private lastDecodeDurationMs = 0.5;
  private mouseX = 640;
  private mouseY = 360;
  private mouseMask = 0;

  private onTelemetryCallback?: (metrics: StreamTelemetryMetrics, rttHistory: number[]) => void;
  private onHardwareStateCallback?: (snapshot: HardwareFrameSnapshot) => void;
  private onStatusChangeCallback?: (status: 'CONNECTING' | 'CONNECTED' | 'RECONNECTING') => void;

  constructor(canvas: HTMLCanvasElement, nodeId: string, engineMode: 'auto' | 'rdp' | 'kvm' = 'auto') {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.ctx = ctx;
    this.nodeId = nodeId;
    this.engineMode = engineMode;

    this.bindInputEvents();
    this.startRenderLoop();
    this.connect();
  }

  public onTelemetry(cb: (metrics: StreamTelemetryMetrics, rttHistory: number[]) => void): void {
    this.onTelemetryCallback = cb;
  }

  public onHardwareState(cb: (snapshot: HardwareFrameSnapshot) => void): void {
    this.onHardwareStateCallback = cb;
  }

  public onConnectionStatus(cb: (status: 'CONNECTING' | 'CONNECTED' | 'RECONNECTING') => void): void {
    this.onStatusChangeCallback = cb;
  }

  public switchTarget(nodeId: string, engineMode: 'auto' | 'rdp' | 'kvm' = 'auto'): void {
    this.nodeId = nodeId;
    this.engineMode = engineMode;
    this.hasRdpFrames = false;
    this.layers.clear();
    this.imageStreams.clear();
    this.connect();
  }

  public setEngineMode(mode: 'rdp' | 'kvm'): void {
    this.engineMode = mode;
    if (mode === 'kvm') {
      this.hasRdpFrames = false;
    }
    this.sendJson({ type: 'SWITCH_ENGINE', engine: mode });
  }

  public sendMacro(macro: string): void {
    this.sendJson({ type: 'MACRO', macro });
  }

  public sendKeystroke(keysym: number, pressed: boolean, label = ''): void {
    this.sendJson({ type: 'KEY', keysym, pressed, label });
  }

  public requestPointerLock(): void {
    if (this.canvas.requestPointerLock) {
      this.canvas.requestPointerLock();
    }
  }

  private getOrCreateLayer(idx: number, w = 1920, h = 1080): StreamLayer {
    let layer = this.layers.get(idx);
    if (!layer) {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const cctx = c.getContext('2d')!;
      layer = { canvas: c, ctx: cctx };
      this.layers.set(idx, layer);
    }
    return layer;
  }

  private connect(): void {
    if (this.destroyed) return;
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
    }
    this.onStatusChangeCallback?.(this.reconnectAttempts > 0 ? 'RECONNECTING' : 'CONNECTING');

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const token = localStorage.getItem('EA_KVM_AUTH_TOKEN') || '';
    const wsUrl = `${proto}//${window.location.host}/api/v1/stream/ws?nodeId=${encodeURIComponent(this.nodeId)}&mode=${this.engineMode}&token=${encodeURIComponent(token)}`;

    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.onStatusChangeCallback?.('CONNECTED');
      if (this.pingTimer) window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        this.sendJson({ type: 'PING', clientTs: performance.now() });
      }, 1500);
    };

    ws.onmessage = (ev: MessageEvent) => {
      if (!(ev.data instanceof ArrayBuffer)) return;
      const bytes = new Uint8Array(ev.data);
      if (bytes.byteLength < 9) return;
      // Validate NXKV magic header (0x4E 0x58 0x4B 0x56)
      if (bytes[0] !== 0x4e || bytes[1] !== 0x58 || bytes[2] !== 0x4b || bytes[3] !== 0x56) return;

      const frameType = bytes[4];
      const view = new DataView(ev.data);
      const seq = view.getUint32(5, false);
      const payloadText = this.textDecoder.decode(bytes.subarray(9));

      const pkt = this.packetPool.acquire(frameType, seq, payloadText);
      try {
        this.processPacket(pkt.frameType, pkt.payloadText);
      } finally {
        this.packetPool.release(pkt);
      }
    };

    ws.onclose = () => {
      if (this.destroyed) return;
      this.onStatusChangeCallback?.('RECONNECTING');
      const backoffMs = Math.min(8000, 400 * Math.pow(1.6, this.reconnectAttempts++));
      this.reconnectTimer = window.setTimeout(() => this.connect(), backoffMs);
    };
  }

  private processPacket(frameType: number, payloadText: string): void {
    const t0 = performance.now();
    if (frameType === 0x01) {
      // RDP_STREAM opcodes
      const parsed = JSON.parse(payloadText) as { engine: string; instructions: string[][] };
      this.applyStreamInstructions(parsed.instructions);
      this.lastDecodeDurationMs = Number((performance.now() - t0).toFixed(2));
    } else if (frameType === 0x02) {
      // KVM_HARDWARE_STATE
      const snap = JSON.parse(payloadText) as HardwareFrameSnapshot;
      this.latestHwSnapshot = snap;
      this.onHardwareStateCallback?.(snap);
    } else if (frameType === 0x03) {
      // TELEMETRY_HUD
      const metrics = JSON.parse(payloadText) as StreamTelemetryMetrics;
      metrics.rttMs = this.measuredRttMs;
      metrics.fpsDecoded = this.currentFps;
      metrics.frameDecodeMs = this.lastDecodeDurationMs;
      this.onTelemetryCallback?.(metrics, this.rttHistory);
    } else if (frameType === 0x04) {
      // PONG
      const pong = JSON.parse(payloadText) as { clientTs: number };
      const rtt = Math.max(0.2, Number((performance.now() - pong.clientTs).toFixed(2)));
      this.measuredRttMs = rtt;
      this.rttHistory.push(rtt);
      if (this.rttHistory.length > 32) this.rttHistory.shift();
    }
  }

  private applyStreamInstructions(instructions: string[][]): void {
    this.instructionChain = this.instructionChain
      .then(async () => {
        for (const inst of instructions) {
          const op = inst[0];
          if (op === 'size') {
            const layerIdx = parseInt(inst[1], 10);
            const w = parseInt(inst[2], 10) || 1920;
            const h = parseInt(inst[3], 10) || 1080;
            const layer = this.getOrCreateLayer(layerIdx, w, h);
            if (layer.canvas.width !== w || layer.canvas.height !== h) {
              const tmp = document.createElement('canvas');
              tmp.width = layer.canvas.width;
              tmp.height = layer.canvas.height;
              tmp.getContext('2d')?.drawImage(layer.canvas, 0, 0);
              layer.canvas.width = w;
              layer.canvas.height = h;
              layer.ctx.drawImage(tmp, 0, 0);
            }
            if (layerIdx === 0 && (this.canvas.width !== w || this.canvas.height !== h)) {
              this.canvas.width = w;
              this.canvas.height = h;
            }
          } else if (op === 'img') {
            const streamIdx = parseInt(inst[1], 10);
            const layerIdx = parseInt(inst[3], 10);
            const mime = inst[4] || 'image/png';
            const x = parseInt(inst[5], 10) || 0;
            const y = parseInt(inst[6], 10) || 0;
            this.imageStreams.set(streamIdx, { layerIdx, x, y, mime, chunks: [] });
          } else if (op === 'blob') {
            const streamIdx = parseInt(inst[1], 10);
            const stream = this.imageStreams.get(streamIdx);
            if (stream && inst[2]) {
              stream.chunks.push(inst[2]);
            }
          } else if (op === 'end') {
            const streamIdx = parseInt(inst[1], 10);
            const stream = this.imageStreams.get(streamIdx);
            if (stream) {
              this.imageStreams.delete(streamIdx);
              const b64 = stream.chunks.join('');
              if (b64.length > 0) {
                await new Promise<void>(resolve => {
                  const img = new Image();
                  img.onload = () => {
                    const targetLayer = this.getOrCreateLayer(
                      stream.layerIdx,
                      Math.max(img.width + stream.x, 64),
                      Math.max(img.height + stream.y, 64)
                    );
                    targetLayer.ctx.drawImage(img, stream.x, stream.y);
                    if (stream.layerIdx === 0) {
                      this.hasRdpFrames = true;
                      this.frameCounter++;
                    }
                    resolve();
                  };
                  img.onerror = () => resolve();
                  img.src = `data:${stream.mime};base64,${b64}`;
                });
              }
            }
          } else if (op === 'rect') {
            const layerIdx = parseInt(inst[1], 10);
            const x = parseInt(inst[2], 10) || 0;
            const y = parseInt(inst[3], 10) || 0;
            const w = parseInt(inst[4], 10) || 0;
            const h = parseInt(inst[5], 10) || 0;
            const layer = this.getOrCreateLayer(layerIdx);
            layer.ctx.beginPath();
            layer.ctx.rect(x, y, w, h);
          } else if (op === 'cfill') {
            const layerIdx = parseInt(inst[2], 10);
            const r = parseInt(inst[3], 10) || 0;
            const g = parseInt(inst[4], 10) || 0;
            const b = parseInt(inst[5], 10) || 0;
            const a = (parseInt(inst[6], 10) ?? 255) / 255;
            const layer = this.getOrCreateLayer(layerIdx);
            layer.ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
            layer.ctx.fill();
            if (layerIdx === 0) {
              this.hasRdpFrames = true;
            }
          } else if (op === 'copy') {
            const srcIdx = parseInt(inst[1], 10);
            const sx = parseInt(inst[2], 10) || 0;
            const sy = parseInt(inst[3], 10) || 0;
            const sw = parseInt(inst[4], 10) || 0;
            const sh = parseInt(inst[5], 10) || 0;
            const dstIdx = parseInt(inst[7], 10);
            const dx = parseInt(inst[8], 10) || 0;
            const dy = parseInt(inst[9], 10) || 0;
            const src = this.layers.get(srcIdx);
            const dst = this.getOrCreateLayer(dstIdx);
            if (src && sw > 0 && sh > 0) {
              dst.ctx.drawImage(src.canvas, sx, sy, sw, sh, dx, dy, sw, sh);
              if (dstIdx === 0) {
                this.hasRdpFrames = true;
                this.frameCounter++;
              }
            }
          } else if (op === 'dispose') {
            const layerIdx = parseInt(inst[1], 10);
            if (layerIdx !== 0) {
              this.layers.delete(layerIdx);
            }
          }
        }
      })
      .catch(() => {
        // Ignore decode chain error
      });
  }

  private startRenderLoop(): void {
    const tick = (now: number) => {
      if (this.destroyed) return;
      if (now - this.lastFpsTick >= 1000) {
        this.currentFps = this.hasRdpFrames ? Math.max(24, Math.min(60, this.frameCounter)) : 60;
        this.frameCounter = 0;
        this.lastFpsTick = now;
      }

      const hw = this.latestHwSnapshot?.hardware;
      if (hw?.privacyMode) {
        this.renderPrivacyShield();
      } else if (this.hasRdpFrames && this.engineMode !== 'kvm' && hw?.powerState === 'S0_ON' && this.latestHwSnapshot?.bios.mode === 'OS_CONSOLE') {
        const rootLayer = this.layers.get(0);
        if (rootLayer) {
          this.ctx.drawImage(rootLayer.canvas, 0, 0, this.canvas.width, this.canvas.height);
        }
      } else {
        this.renderHardwareKvmSurface(now);
      }

      this.rafId = window.requestAnimationFrame(tick);
    };
    this.rafId = window.requestAnimationFrame(tick);
  }

  private renderPrivacyShield(): void {
    const w = this.canvas.width;
    const h = this.canvas.height;
    this.ctx.fillStyle = '#090B0E';
    this.ctx.fillRect(0, 0, w, h);
    this.ctx.strokeStyle = '#1E2430';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(w / 2 - 260, h / 2 - 80, 520, 160);
    this.ctx.fillStyle = '#101318';
    this.ctx.fillRect(w / 2 - 259, h / 2 - 79, 518, 158);
    this.ctx.fillStyle = '#EF4444';
    this.ctx.font = '600 15px "JetBrains Mono", monospace';
    this.ctx.fillText('[PRIVACY MODE ACTIVE — REMOTE DISPLAY BLACKOUT]', w / 2 - 220, h / 2 - 20);
    this.ctx.fillStyle = '#9CA3AF';
    this.ctx.font = '12px "Inter", sans-serif';
    this.ctx.fillText('Physical monitor blanked & local HID inputs locked during administrator intervention.', w / 2 - 220, h / 2 + 12);
    this.ctx.fillStyle = '#6B7280';
    this.ctx.font = '11px "JetBrains Mono", monospace';
    this.ctx.fillText('Toggle Privacy Mode off in the top toolbar to restore visual output.', w / 2 - 220, h / 2 + 42);
  }

  private renderHardwareKvmSurface(now: number): void {
    const snap = this.latestHwSnapshot;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const ctx = this.ctx;

    ctx.fillStyle = '#090B0E';
    ctx.fillRect(0, 0, w, h);

    if (!snap) {
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '13px "JetBrains Mono", monospace';
      ctx.fillText('INITIALIZING NXKV BINARY STREAM ENGINE...', 48, 64);
      return;
    }

    const pwr = snap.hardware.powerState;
    if (pwr === 'S5_OFF' || pwr === 'S3_SLEEP') {
      ctx.strokeStyle = '#1E2430';
      ctx.strokeRect(w / 2 - 250, h / 2 - 70, 500, 140);
      ctx.fillStyle = '#EF4444';
      ctx.font = '600 14px "JetBrains Mono", monospace';
      ctx.fillText(`NO HDMI/DP SIGNAL — TARGET ACPI STATE: ${pwr}`, w / 2 - 210, h / 2 - 15);
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '12px "JetBrains Mono", monospace';
      ctx.fillText(`Target: ${snap.hostname} (${snap.ip}) | Board 5VSB Standby Active`, w / 2 - 210, h / 2 + 15);
      ctx.fillStyle = '#3B82F6';
      ctx.fillText('Trigger ATX Power Short or Wake-on-LAN in the right dock to power on.', w / 2 - 210, h / 2 + 42);
      return;
    }

    const bios = snap.bios;
    if (pwr === 'RESETTING' || bios.mode === 'BIOS_POST') {
      ctx.fillStyle = '#F3F4F6';
      ctx.font = '600 15px "JetBrains Mono", monospace';
      ctx.fillText('EA-KVM ENTERPRISE BARE-METAL UEFI BIOS v2.42 (American Megatrends Core)', 48, 56);
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '13px "JetBrains Mono", monospace';
      ctx.fillText(`CPU : ${snap.cpuModel}`, 48, 92);
      ctx.fillText(`MEM : ${snap.ramTotalMb} MB ECC DDR4/DDR5 OK (Dual-Channel Interleaved)`, 48, 116);
      ctx.fillText(`USB :HID Keyboard + Absolute/Relative Pointer Emulation Initialized`, 48, 140);
      ctx.fillText(`VMD : ${snap.hardware.mountedMedia ? `${snap.hardware.mountedMedia.name} [${snap.hardware.mountedMedia.loopDevice}]` : 'No Virtual ISO Mounted'}`, 48, 164);
      ctx.fillStyle = '#10B981';
      ctx.fillText('Press [F2] or [DEL] to enter UEFI BIOS Setup  |  Press [F12] for Boot Override Menu', 48, h - 56);
      return;
    }

    if (bios.mode === 'BIOS_SETUP') {
      // Classic High-Precision Bare-Metal UEFI Setup Utility
      ctx.fillStyle = '#101318';
      ctx.fillRect(24, 24, w - 48, h - 48);
      ctx.strokeStyle = '#3B82F6';
      ctx.lineWidth = 1;
      ctx.strokeRect(24, 24, w - 48, h - 48);

      ctx.fillStyle = '#161A22';
      ctx.fillRect(25, 25, w - 50, 38);
      ctx.fillStyle = '#F3F4F6';
      ctx.font = '600 13px "JetBrains Mono", monospace';
      ctx.fillText(`UEFI BIOS SETUP UTILITY — ${snap.hostname.toUpperCase()} (${snap.edid.width}x${snap.edid.height}@${snap.edid.refreshHz}Hz)`, 42, 49);

      const tabs = ['MAIN', 'ADVANCED / ACPI', 'VIRTUAL MEDIA (ISO)', 'BOOT OVERRIDE'];
      tabs.forEach((t, idx) => {
        const tx = 42 + idx * 195;
        ctx.fillStyle = bios.selectedTab === idx ? '#3B82F6' : '#101318';
        ctx.fillRect(tx, 74, 182, 28);
        ctx.strokeStyle = '#1E2430';
        ctx.strokeRect(tx, 74, 182, 28);
        ctx.fillStyle = bios.selectedTab === idx ? '#F3F4F6' : '#9CA3AF';
        ctx.font = '600 11px "JetBrains Mono", monospace';
        ctx.fillText(t, tx + 14, 92);
      });

      const rows = [
        ['System Firmware Build', 'EA-KVM-UEFI-2026.10 (SecureBoot: Custom Mode)'],
        ['Target Processor', `${snap.cpuModel} (${snap.hardware.cpuTempC} C)`],
        ['Board Telemetry', `${snap.hardware.boardVoltageV} V | Cooling Fan: ${snap.hardware.fanRpm} RPM`],
        ['Virtual CD-ROM / USB Drive', snap.hardware.mountedMedia ? `${snap.hardware.mountedMedia.name} (${snap.hardware.mountedMedia.readOnly ? 'RO' : 'RW'})` : 'Not Mounted'],
        ['Action: Boot Mounted Virtual ISO / OS', 'Press [ENTER] or [F10] to Boot Selected Image']
      ];

      rows.forEach((r, rIdx) => {
        const ry = 128 + rIdx * 42;
        if (bios.selectedRow === rIdx) {
          ctx.fillStyle = 'rgba(59, 130, 246, 0.16)';
          ctx.fillRect(42, ry - 18, w - 84, 32);
          ctx.strokeStyle = '#3B82F6';
          ctx.strokeRect(42, ry - 18, w - 84, 32);
        }
        ctx.fillStyle = '#9CA3AF';
        ctx.font = '12px "JetBrains Mono", monospace';
        ctx.fillText(r[0], 56, ry);
        ctx.fillStyle = rIdx === 4 ? '#10B981' : '#F3F4F6';
        ctx.fillText(r[1], 360, ry);
      });

      ctx.fillStyle = '#161A22';
      ctx.fillRect(42, h - 135, w - 84, 54);
      ctx.strokeStyle = '#1E2430';
      ctx.strokeRect(42, h - 135, w - 84, 54);
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '11px "JetBrains Mono", monospace';
      ctx.fillText(`LAST HID KEY RECEIVED: ${bios.lastKeyLabel}  |  PASTE-AS-KEYSTROKES BUFFER: ${(bios.typedBuffer || '<empty>').slice(-48)}`, 56, h - 104);

      ctx.fillStyle = '#6B7280';
      ctx.fillText('[Arrows] Navigate   [Enter/F10] Boot Virtual ISO / Save   [F12] Boot Menu   [ESC] Exit to Live OS Console', 42, h - 46);
      return;
    }

    if (bios.mode === 'BOOT_MENU') {
      ctx.fillStyle = '#101318';
      ctx.fillRect(w / 2 - 300, h / 2 - 150, 600, 300);
      ctx.strokeStyle = '#3B82F6';
      ctx.strokeRect(w / 2 - 300, h / 2 - 150, 600, 300);
      ctx.fillStyle = '#F3F4F6';
      ctx.font = '600 13px "JetBrains Mono", monospace';
      ctx.fillText('UEFI BOOT MANAGER — SELECT BOOTOVERRIDE DEVICE', w / 2 - 270, h / 2 - 115);

      const bootOptions = [
        `1. NVMe0n1 — Primary OS (${snap.osVersion})`,
        `2. Virtual Media CD/USB — ${snap.hardware.mountedMedia ? snap.hardware.mountedMedia.name : '[Mount ISO in Virtual Media Panel]'}`,
        `3. PXE IPv4 Network Boot (${snap.ip} / netboot.xyz)`,
        `4. Enter UEFI Setup Utility (F2)`
      ];
      bootOptions.forEach((opt, idx) => {
        const oy = h / 2 - 65 + idx * 42;
        if (bios.selectedRow === idx) {
          ctx.fillStyle = 'rgba(16, 185, 129, 0.18)';
          ctx.fillRect(w / 2 - 275, oy - 20, 550, 32);
          ctx.strokeStyle = '#10B981';
          ctx.strokeRect(w / 2 - 275, oy - 20, 550, 32);
        }
        ctx.fillStyle = bios.selectedRow === idx ? '#10B981' : '#F3F4F6';
        ctx.font = '12px "JetBrains Mono", monospace';
        ctx.fillText(opt, w / 2 - 260, oy);
      });
      ctx.fillStyle = '#9CA3AF';
      ctx.fillText('[Up/Down] Select Device   |   [Enter] Boot Now   |   [ESC] Cancel', w / 2 - 270, h / 2 + 125);
      return;
    }

    // OS_CONSOLE or ISO_LIVE_BOOT interactive hardware KVM view
    ctx.fillStyle = '#101318';
    ctx.fillRect(0, 0, w, 34);
    ctx.strokeStyle = '#1E2430';
    ctx.beginPath();
    ctx.moveTo(0, 34);
    ctx.lineTo(w, 34);
    ctx.stroke();

    ctx.fillStyle = '#10B981';
    ctx.font = '600 12px "JetBrains Mono", monospace';
    const headerTitle = bios.mode === 'ISO_LIVE_BOOT'
      ? `LIVE VIRTUAL MEDIA BOOT: ${bios.bootIsoName || snap.hardware.mountedMedia?.name || 'ISO'}`
      : `HARDWARE IP-KVM DIRECT CONSOLE — ${snap.hostname} (${snap.ip})`;
    ctx.fillText(headerTitle, 18, 22);

    ctx.fillStyle = '#9CA3AF';
    ctx.font = '11px "JetBrains Mono", monospace';
    ctx.fillText(`EDID: ${snap.edid.width}x${snap.edid.height}@${snap.edid.refreshHz}Hz | ACPI: ${snap.hardware.powerState} | CPU: ${snap.cpuPercent.toFixed(1)}%`, w - 470, 22);

    const lines = [
      `Linux ${snap.hostname} 6.17.0-ea-kvm #1 SMP PREEMPT_DYNAMIC x86_64 GNU/Linux`,
      `Hardware Bridge : NXKV Binary Multiplexer v3.0 | Zero-Allocation Canvas Pool @ ${this.currentFps} FPS`,
      `Target Node     : ${snap.hostname} | IPv4: ${snap.ip} | OS: ${snap.osVersion}`,
      `ATX / Sensors   : Power=${snap.hardware.powerState} | Temp=${snap.hardware.cpuTempC}C | +12V=${snap.hardware.boardVoltageV}V | Fan=${snap.hardware.fanRpm}RPM`,
      `Virtual Media   : ${snap.hardware.mountedMedia ? `${snap.hardware.mountedMedia.name} on ${snap.hardware.mountedMedia.loopDevice} (${snap.hardware.mountedMedia.readSpeedKbps} KB/s)` : 'Unmounted (Ready for ISO/IMG/HTTP URL)'}`,
      `HID Controller  : Mode=${snap.hardware.mouseMode} | Last Key=${bios.lastKeyLabel} | Pointer=(${Math.round(this.mouseX)}, ${Math.round(this.mouseY)})`,
      `BIOS Shortcuts  : Press [F2] for UEFI Setup | [F12] for Boot Override | Use Paste-as-Keystrokes in Right Dock`,
      `--------------------------------------------------------------------------------------------------------`
    ];

    ctx.font = '12.5px "JetBrains Mono", monospace';
    lines.forEach((l, idx) => {
      ctx.fillStyle = idx === 0 ? '#F3F4F6' : idx === 6 ? '#3B82F6' : '#9CA3AF';
      ctx.fillText(l, 24, 68 + idx * 24);
    });

    // Live interactive shell buffer area
    ctx.fillStyle = '#10B981';
    ctx.fillText(`root@${snap.hostname}:~# `, 24, 280);
    const userLines = (bios.typedBuffer || 'systemctl status ea-kstreamd --no-pager\n● ea-kstreamd.service - Active (running) on 127.0.0.1:4822').split('\n').slice(-12);
    userLines.forEach((ul, idx) => {
      ctx.fillStyle = '#F3F4F6';
      ctx.fillText(ul, idx === 0 ? 225 : 24, 280 + idx * 22);
    });

    // Blinking block cursor
    if (Math.floor(now / 450) % 2 === 0) {
      const lastLine = userLines[userLines.length - 1] || '';
      const cx = (userLines.length === 1 ? 225 : 24) + lastLine.length * 7.6;
      const cy = 266 + (userLines.length - 1) * 22;
      ctx.fillStyle = '#3B82F6';
      ctx.fillRect(cx, cy, 8, 16);
    }

    // Precision crosshair for hardware mouse position
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.65)';
    ctx.beginPath();
    ctx.arc(this.mouseX, this.mouseY, 6, 0, Math.PI * 2);
    ctx.stroke();
  }

  private bindInputEvents(): void {
    const computeCoords = (e: MouseEvent) => {
      const rect = this.canvas.getBoundingClientRect();
      if (document.pointerLockElement === this.canvas) {
        this.mouseX = Math.max(0, Math.min(this.canvas.width, this.mouseX + e.movementX));
        this.mouseY = Math.max(0, Math.min(this.canvas.height, this.mouseY + e.movementY));
      } else {
        const scaleX = this.canvas.width / Math.max(1, rect.width);
        const scaleY = this.canvas.height / Math.max(1, rect.height);
        this.mouseX = Math.round((e.clientX - rect.left) * scaleX);
        this.mouseY = Math.round((e.clientY - rect.top) * scaleY);
      }
    };

    this.canvas.addEventListener('mousemove', e => {
      computeCoords(e);
      this.sendJson({ type: 'MOUSE', x: this.mouseX, y: this.mouseY, mask: this.mouseMask });
    });

    this.canvas.addEventListener('mousedown', e => {
      this.canvas.focus();
      computeCoords(e);
      if (e.button === 0) this.mouseMask |= 1;
      if (e.button === 1) this.mouseMask |= 2;
      if (e.button === 2) this.mouseMask |= 4;
      this.sendJson({ type: 'MOUSE', x: this.mouseX, y: this.mouseY, mask: this.mouseMask });
    });

    this.canvas.addEventListener('mouseup', e => {
      computeCoords(e);
      if (e.button === 0) this.mouseMask &= ~1;
      if (e.button === 1) this.mouseMask &= ~2;
      if (e.button === 2) this.mouseMask &= ~4;
      this.sendJson({ type: 'MOUSE', x: this.mouseX, y: this.mouseY, mask: this.mouseMask });
    });

    this.canvas.addEventListener('wheel', e => {
      e.preventDefault();
      const mask = e.deltaY < 0 ? 8 : 16;
      this.sendJson({ type: 'MOUSE', x: this.mouseX, y: this.mouseY, mask });
      this.sendJson({ type: 'MOUSE', x: this.mouseX, y: this.mouseY, mask: 0 });
    }, { passive: false });

    this.canvas.addEventListener('contextmenu', e => e.preventDefault());

    this.canvas.addEventListener('keydown', e => {
      // Allow Ctrl+K / Cmd+K global command palette shortcut to bubble
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') return;
      e.preventDefault();
      const ks = this.domKeyToX11Keysym(e);
      this.sendJson({ type: 'KEY', keysym: ks, pressed: true, label: e.key });
    });

    this.canvas.addEventListener('keyup', e => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') return;
      e.preventDefault();
      const ks = this.domKeyToX11Keysym(e);
      this.sendJson({ type: 'KEY', keysym: ks, pressed: false, label: e.key });
    });
  }

  private domKeyToX11Keysym(e: KeyboardEvent): number {
    const special: Record<string, number> = {
      Backspace: 0xff08,
      Tab: 0xff09,
      Enter: 0xff0d,
      Escape: 0xff1b,
      Delete: 0xffff,
      Home: 0xff50,
      ArrowLeft: 0xff51,
      ArrowUp: 0xff52,
      ArrowRight: 0xff53,
      ArrowDown: 0xff54,
      PageUp: 0xff55,
      PageDown: 0xff56,
      End: 0xff57,
      Insert: 0xff63,
      F1: 0xffbe,
      F2: 0xffbf,
      F3: 0xffc0,
      F4: 0xffc1,
      F5: 0xffc2,
      F6: 0xffc3,
      F7: 0xffc4,
      F8: 0xffc5,
      F9: 0xffc6,
      F10: 0xffc7,
      F11: 0xffc8,
      F12: 0xffc9,
      Shift: 0xffe1,
      Control: 0xffe3,
      Alt: 0xffe9,
      Meta: 0xffeb
    };
    if (special[e.key]) return special[e.key];
    if (e.key.length === 1) return e.key.charCodeAt(0);
    return 0x0020;
  }

  private sendJson(payload: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  public destroy(): void {
    this.destroyed = true;
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    if (this.pingTimer) window.clearInterval(this.pingTimer);
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
    }
  }
}
